import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { requestOtp, verifyOtp } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { useSignupStore } from '@shared/stores/signup-store';
import { ME_QUERY_KEY } from '@shared/hooks/use-me';
import type { MyProfile, UserRole } from '@shared/types/api';
import { AuthShell } from './auth-shell';

// Admin is intentionally absent — the public /auth/otp/verify endpoint
// refuses ADMIN accounts server-side. Admins log in via the admin console.
type PublicRole = Exclude<UserRole, 'ADMIN'>;

/**
 * Destination on a successful login. Driven purely by the server-returned
 * role — whatever the email is bound to is the source of truth. URL intent
 * (`?as=associate`) is intentionally NOT consulted here: if an email is a
 * client it goes to /client/home, if it's an associate it goes to
 * /associate/transactions, regardless of which button the user clicked.
 */
function landingForRole(role: PublicRole): string {
  if (role === 'ASSOCIATE' || role === 'FRANCHISE') return '/associate/transactions';
  return '/client/home';
}

export function OtpPage() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  // `?as=associate` is consulted ONLY when the email isn't registered yet
  // (SIGNUP_REQUIRED) — we need it to pick the right onboarding form.
  // Existing users ignore it; their role is read off the server response.
  const isAssociateFlow = params.get('as') === 'associate';
  const isSignupFlow = params.get('signup') === 'true';

  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(30);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const setSignup = useSignupStore((s) => s.set);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const verify = useMutation({
    mutationFn: () => verifyOtp(email, code, isAssociateFlow ? 'ASSOCIATE' : 'CLIENT'),
    onSuccess: (data) => {
      if (data.status === 'LOGGED_IN') {
        // Server rejects ADMIN accounts on this endpoint, so `role` here is
        // always a public role. TS narrowing via our PublicRole alias.
        const role = data.user.role as PublicRole;

        const scope = role === 'ASSOCIATE' || role === 'FRANCHISE' ? 'associate' : 'client';
        getTokenStore().setAccessToken(data.accessToken, scope);
        setSession(data.user);
        // Prime the useMe() cache with the NEW user BEFORE we navigate.
        // Otherwise layouts (ClientLayout / AssociateLayout) re-render
        // with the previous session's stale `me` row first and a role
        // gate can bounce the user to the wrong app.
        qc.setQueryData(ME_QUERY_KEY, {
          ...data.user,
          avatarUrl: null,
          emailVerifiedAt: null,
          lastLoginAt: null,
          createdAt: new Date().toISOString(),
        } as MyProfile);
        qc.invalidateQueries({ queryKey: ME_QUERY_KEY });

        // Role-driven, no mismatch notice, no URL-intent fallback. The
        // email determines the destination, period. An email can only
        // ever be one role server-side, so this is unambiguous.
        navigate(landingForRole(role), { replace: true });
      } else {
        setSignup({ signupToken: data.signupToken, email });
        navigate(isAssociateFlow ? '/associate/onboarding' : '/client/onboarding', {
          replace: true,
        });
      }
    },
  });

  const resend = useMutation({
    mutationFn: () => requestOtp(email, isAssociateFlow ? 'ASSOCIATE' : 'CLIENT', isSignupFlow),
    // Reset the cooldown on every outcome — a failed resend shouldn't let
    // the user keep mashing the button. Server's per-email 30s lockout
    // would reject anyway, but gating client-side stops the churn.
    onSettled: () => setSecondsLeft(30),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || !email) return;
    verify.mutate();
  }

  const backTo = isAssociateFlow ? '/associate/join' : '/login';

  return (
    <AuthShell>
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">Verify Your Email</h1>
          <p className="mt-2 text-sm text-gray-600">
            We sent a temporary login code to <span className="font-semibold">{email}</span>.
          </p>
          <Link
            to={backTo}
            className="mt-1 inline-block text-sm text-gray-700 underline underline-offset-2"
          >
            Not you?
          </Link>
        </div>

        <form className="space-y-3" onSubmit={onSubmit}>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="Enter 6 digit OTP"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          <div className="text-xs text-gray-500">
            Didn&rsquo;t get it?{' '}
            {secondsLeft > 0 ? (
              <span>Resend OTP (in {secondsLeft}s)</span>
            ) : (
              <button
                type="button"
                className="underline underline-offset-2 disabled:opacity-50"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
              >
                {resend.isPending ? 'Resending…' : 'Resend OTP'}
              </button>
            )}
          </div>
          {verify.isError ? (
            <p className="text-sm text-red-600">
              {apiErrorMessage(verify.error, 'Invalid or expired OTP')}
            </p>
          ) : null}
          <Button type="submit" block disabled={code.length !== 6 || verify.isPending}>
            {verify.isPending ? 'Verifying…' : 'Continue'}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
